import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDate, todayISO } from "@/lib/api";
import { Field } from "@/components/Field";
import { MonthCalendar, monthRange } from "@/components/MonthCalendar";

const CATS = { personale: { label: "Personale", tone: "violet" }, studio: { label: "Studio", tone: "dark" },
  formazione: { label: "Formazione", tone: "amber" }, altro: { label: "Altro", tone: "rose" } };
const blank = (date) => ({ title: "", date: date || todayISO(), time: "", end_time: "", category: "personale", notes: "" });

const EventDialog = ({ open, onOpenChange, initial, date, onSaved }) => {
  const [f, setF] = useState(blank());
  useEffect(() => { if (open) setF(initial ? { ...blank(), ...initial } : blank(date)); }, [open, initial, date]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const { id, created_at, ...body } = f;
      initial ? await api.put(`/agenda/${initial.id}`, body) : await api.post("/agenda", body);
      toast.success("Impegno salvato");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async () => {
    await api.delete(`/agenda/${initial.id}`);
    onOpenChange(false);
    onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="agenda-dialog">
        <DialogHeader><DialogTitle>{initial ? "Modifica impegno" : "Nuovo impegno"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Titolo"><input required className="field" value={f.title} onChange={set("title")} data-testid="agenda-title-input" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Data"><input type="date" required className="field" value={f.date} onChange={set("date")} data-testid="agenda-date-input" /></Field>
            <Field label="Dalle"><input type="time" className="field" value={f.time} onChange={set("time")} data-testid="agenda-time-input" /></Field>
            <Field label="Alle"><input type="time" className="field" value={f.end_time} onChange={set("end_time")} data-testid="agenda-end-time-input" /></Field>
          </div>
          <Field label="Categoria">
            <select className="field" value={f.category} onChange={set("category")} data-testid="agenda-category-select">
              {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Note"><textarea className="field min-h-[70px]" value={f.notes} onChange={set("notes")} data-testid="agenda-notes-input" /></Field>
          <div className="flex justify-between">
            {initial ? <button type="button" onClick={remove} className="btn-ghost text-rose-600" data-testid="agenda-delete-button"><Trash2 className="h-4 w-4" /> Elimina</button> : <span />}
            <button className="btn-dark" data-testid="agenda-save-button">Salva</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Agenda() {
  const now = new Date();
  const [month, setMonth] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(todayISO());
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/agenda", { params: monthRange(month) }).then((r) => setItems(r.data)), [month]);
  useEffect(() => { load(); }, [load]);

  const events = items.map((e) => ({ id: e.id, date: e.date, time: e.time, label: e.title, tone: CATS[e.category]?.tone, raw: e }));
  const dayItems = items.filter((e) => e.date === selected);

  return (
    <div className="fade-up space-y-8" data-testid="agenda-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-xs flex items-center gap-1 text-[#3d655b]"><Lock className="h-3 w-3" /> Riservato alla dottoressa</div>
          <h1 className="page-title mt-1">Agenda personale</h1>
        </div>
        <button className="btn-dark" onClick={() => { setEdit(null); setOpen(true); }} data-testid="agenda-new-button"><Plus className="h-4 w-4" /> Nuovo impegno</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.entries(CATS).map(([k, v]) => <span key={k} className="label-xs rounded-full bg-white px-3 py-1 shadow-sm">{v.label}</span>)}
      </div>
      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <MonthCalendar month={month} onMonthChange={setMonth} events={events} selected={selected} onSelectDay={setSelected}
          onEventClick={(e) => { setEdit(e.raw); setOpen(true); }} prefix="agenda" />
        <div className="card h-fit" data-testid="agenda-day-panel">
          <h3 className="text-lg font-bold capitalize">{fmtDate(selected, { weekday: "long", day: "numeric", month: "long" })}</h3>
          <div className="mt-4 space-y-3">
            {dayItems.length ? dayItems.map((e) => (
              <button key={e.id} onClick={() => { setEdit(e); setOpen(true); }} className="w-full rounded-xl border border-[#e2e8f0] p-3 text-left hover:border-slate-400" data-testid={`agenda-day-item-${e.id}`}>
                <div className="flex justify-between gap-2"><span className="font-semibold">{e.title}</span><span className="text-sm text-slate-500">{e.time}{e.end_time ? `–${e.end_time}` : ""}</span></div>
                <div className="label-xs mt-1">{CATS[e.category]?.label}</div>
                {e.notes && <p className="mt-1 text-xs text-slate-500">{e.notes}</p>}
              </button>
            )) : <p className="text-sm text-slate-400">Nessun impegno.</p>}
          </div>
          <button className="btn-outline mt-4 w-full" onClick={() => { setEdit(null); setOpen(true); }} data-testid="agenda-day-add"><Plus className="h-4 w-4" /> Aggiungi</button>
        </div>
      </div>
      <EventDialog open={open} onOpenChange={setOpen} initial={edit} date={selected} onSaved={load} />
    </div>
  );
}
